import { generateClientUuid } from './clientUuid'

const CHECKOUT_LOCK_NAME = 'erp-pos:checkout:v2'
const CHECKOUT_LEASE_KEY = 'erp_pos_checkout_lock_v2'
const CHECKOUT_LEASE_MS = 120_000

let checkoutInFlight = false

export interface AcquiredCheckoutLock {
  release: () => void
}

export function tryAcquireCheckoutLock(): boolean {
  if (checkoutInFlight) return false
  checkoutInFlight = true
  return true
}

export function releaseCheckoutLock(): void {
  checkoutInFlight = false
}

export function isCheckoutInFlight(): boolean {
  return checkoutInFlight
}

function readLease(): { owner: string; expiresAt: number } | null {
  try {
    const raw = localStorage.getItem(CHECKOUT_LEASE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { owner?: unknown; expiresAt?: unknown }
    if (typeof parsed.owner !== 'string' || typeof parsed.expiresAt !== 'number') return null
    return { owner: parsed.owner, expiresAt: parsed.expiresAt }
  } catch {
    return null
  }
}

function acquireLease(): AcquiredCheckoutLock | null {
  if (typeof localStorage === 'undefined') {
    if (!tryAcquireCheckoutLock()) return null
    return { release: releaseCheckoutLock }
  }
  if (checkoutInFlight) return null
  const owner = generateClientUuid()
  const now = Date.now()
  const current = readLease()
  if (current && current.expiresAt > now) return null
  try {
    localStorage.setItem(CHECKOUT_LEASE_KEY, JSON.stringify({ owner, expiresAt: now + CHECKOUT_LEASE_MS }))
    if (readLease()?.owner !== owner) return null
  } catch {
    if (!tryAcquireCheckoutLock()) return null
    return { release: releaseCheckoutLock }
  }
  checkoutInFlight = true
  const renew = setInterval(() => {
    if (readLease()?.owner !== owner) return
    try {
      localStorage.setItem(
        CHECKOUT_LEASE_KEY,
        JSON.stringify({ owner, expiresAt: Date.now() + CHECKOUT_LEASE_MS }),
      )
    } catch {
      /* The lease still expires safely if storage becomes unavailable. */
    }
  }, CHECKOUT_LEASE_MS / 3)
  return {
    release: () => {
      clearInterval(renew)
      if (readLease()?.owner === owner) {
        try { localStorage.removeItem(CHECKOUT_LEASE_KEY) } catch { /* expiry releases it */ }
      }
      checkoutInFlight = false
    },
  }
}

/** Cross-tab checkout mutex using Web Locks, with a renewable storage lease fallback. */
export async function acquireCheckoutLock(scope = 'default'): Promise<AcquiredCheckoutLock | null> {
  if (checkoutInFlight) return null
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks?.request) return acquireLease()

  let releaseHold!: () => void
  let resolveAcquired!: (value: AcquiredCheckoutLock | null) => void
  const acquired = new Promise<AcquiredCheckoutLock | null>((resolve) => { resolveAcquired = resolve })
  const hold = new Promise<void>((resolve) => { releaseHold = resolve })

  void locks.request(
    `${CHECKOUT_LOCK_NAME}:${scope}`,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock || checkoutInFlight) {
        resolveAcquired(null)
        return
      }
      checkoutInFlight = true
      let released = false
      resolveAcquired({
        release: () => {
          if (released) return
          released = true
          checkoutInFlight = false
          releaseHold()
        },
      })
      await hold
    },
  ).catch(() => resolveAcquired(acquireLease()))

  return acquired
}
